import React, { useMemo, useState } from "react";
import { Box, Typography, TextField, Table, TableBody, TableCell, TableHead, TableRow, Stack } from "@mui/material";

// Admin pricing calculator — mirrors speakhere-api/src/tasks/pricing-model.ts
// (usage-based pass-through: flat per-session fee both sides + annual fee;
// mutualiteit refund applies only to self-paid sessions beyond the fair-use cap).
// Internal tool (platform_admin only) — labels are intentionally not i18n'd.

const eur = (n: number) => `€${(isFinite(n) ? n : 0).toFixed(2)}`;
const num = (v: string, fallback: number) => {
  const n = Number(v.replace(",", "."));
  return isFinite(n) ? n : fallback;
};

type Field = { key: string; label: string; default: number; suffix?: string };

const FIELDS: Field[] = [
  { key: "adoption", label: "Adoptie (% werknemers dat sessies neemt)", default: 10, suffix: "%" },
  { key: "sessions", label: "Gem. sessies / actieve gebruiker / jaar", default: 4 },
  { key: "headcount", label: "Aantal werknemers", default: 200 },
  { key: "coachingShare", label: "Aandeel coaching", default: 50, suffix: "%" },
  { key: "coachingRate", label: "Coaching-tarief", default: 37.5, suffix: "€" },
  { key: "therapyRate", label: "Therapie-tarief", default: 75, suffix: "€" },
  { key: "coachingFee", label: "Fee/kant coaching", default: 2, suffix: "€" },
  { key: "therapyFee", label: "Fee/kant therapie", default: 5, suffix: "€" },
  { key: "annualFee", label: "Vaste jaarlijkse fee / werkgever", default: 1500, suffix: "€" },
  { key: "cap", label: "Fair-use cap (gefinancierd/wn/jaar)", default: 6 },
  { key: "mutualiteit", label: "Mutualiteit-terugbetaling / zelf-betaalde sessie", default: 20, suffix: "€" },
];

// Employer-funded = fee both sides (margin 2×fee); client-paid (B2C / self-paid
// beyond cap) = provider side only (margin 1×fee, no payer surcharge — decision 2026-07).
const perType = (rate: number, fee: number, mut: number) => ({
  employerCost: rate + fee,
  providerPayout: rate - fee,
  fundedMargin: 2 * fee,
  selfPaidMargin: fee,
  employeeSelfPayNet: rate - mut,
});

function calc(v: Record<string, number>) {
  const ad = v.adoption / 100;
  const cShare = v.coachingShare / 100;
  const blendRate = cShare * v.coachingRate + (1 - cShare) * v.therapyRate;
  const blendFee = cShare * v.coachingFee + (1 - cShare) * v.therapyFee;

  const coach = perType(v.coachingRate, v.coachingFee, v.mutualiteit);
  const ther = perType(v.therapyRate, v.therapyFee, v.mutualiteit);
  const bl = perType(blendRate, blendFee, v.mutualiteit);

  const activeUsers = v.headcount * ad;
  const fundedPerUser = Math.min(v.sessions, v.cap);
  const selfPaidPerUser = Math.max(0, v.sessions - v.cap);
  const fundedSessions = activeUsers * fundedPerUser;
  const selfPaidSessions = activeUsers * selfPaidPerUser;

  const employerInvoice = fundedSessions * bl.employerCost + v.annualFee;
  const ourMarginYr = fundedSessions * bl.fundedMargin + selfPaidSessions * bl.selfPaidMargin + v.annualFee;
  const employeeOOP = selfPaidSessions * bl.employeeSelfPayNet;

  return {
    coach, ther, bl, activeUsers, fundedSessions, selfPaidSessions,
    employerInvoice, ourMarginYr, employeeOOP,
    realizedPerEmp: v.headcount ? employerInvoice / v.headcount : 0,
    // sensitivity
    sens: [0.05, 0.08, 0.1, 0.12, 0.15, 0.2, 0.25].map((a) => {
      const au = v.headcount * a;
      const funded = au * fundedPerUser;
      const self = au * selfPaidPerUser;
      const invoice = funded * bl.employerCost + v.annualFee;
      const margin = funded * bl.fundedMargin + self * bl.selfPaidMargin + v.annualFee;
      return { a, funded, costPerEmp: v.headcount ? invoice / v.headcount : 0, margin };
    }),
  };
}

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>{children}</Box>
);
const Metric: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.4 }}>
    <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>{label}</Typography>
    <Typography sx={{ fontSize: ".9rem", fontWeight: strong ? 700 : 500, whiteSpace: "nowrap" }}>{value}</Typography>
  </Box>
);

export default function PricingCalculator() {
  const [raw, setRaw] = useState<Record<string, string>>(
    Object.fromEntries(FIELDS.map((f) => [f.key, String(f.default)]))
  );
  const v = useMemo(() => Object.fromEntries(FIELDS.map((f) => [f.key, num(raw[f.key] ?? "", f.default)])), [raw]);
  const r = useMemo(() => calc(v as Record<string, number>), [v]);

  return (
    <Stack spacing={3}>
      <Typography sx={{ color: "text.secondary", fontSize: ".9rem" }}>
        Usage-based pass-through: werkgever betaalt per verbruikte sessie (tarief + vaste fee aan beide kanten)
        + een vaste jaarlijkse fee. Mutualiteit-terugbetaling geldt enkel op zelf-betaalde sessies boven de fair-use cap.
        Conventionering/RIZIV is (voorlopig) niet meegerekend.
      </Typography>

      {/* Inputs */}
      <Card>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
          {FIELDS.map((f) => (
            <TextField
              key={f.key} size="small" type="number" label={f.label}
              value={raw[f.key]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRaw((p) => ({ ...p, [f.key]: e.target.value }))}
              InputProps={f.suffix ? { endAdornment: <Typography sx={{ color: "text.secondary", fontSize: ".8rem" }}>{f.suffix}</Typography> } : undefined}
            />
          ))}
        </Box>
      </Card>

      {/* Per-session economics */}
      <Card>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Per sessie</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">coaching</TableCell>
              <TableCell align="right">therapie</TableCell>
              <TableCell align="right">blend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ["Kost werkgever (gefinancierd)", r.coach.employerCost, r.ther.employerCost, r.bl.employerCost],
              ["Provider-payout (van ons)", r.coach.providerPayout, r.ther.providerPayout, r.bl.providerPayout],
              ["Onze marge — gefinancierde sessie", r.coach.fundedMargin, r.ther.fundedMargin, r.bl.fundedMargin],
              ["Onze marge — zelf-betaalde sessie", r.coach.selfPaidMargin, r.ther.selfPaidMargin, r.bl.selfPaidMargin],
              ["Werknemer netto indien zelf-betaald*", r.coach.employeeSelfPayNet, r.ther.employeeSelfPayNet, r.bl.employeeSelfPayNet],
            ].map(([label, c, t, b]) => (
              <TableRow key={label as string}>
                <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
                <TableCell align="right">{eur(c as number)}</TableCell>
                <TableCell align="right">{eur(t as number)}</TableCell>
                <TableCell align="right"><strong>{eur(b as number)}</strong></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography sx={{ fontSize: ".78rem", color: "text.secondary", mt: 1 }}>
          * boven de fair-use cap betaalt de werknemer zelf (= B2C): tarief − mutualiteit-terugbetaling, géén surcharge. Onze marge op die sessies is enkel de provider-kant-fee.
        </Typography>
      </Card>

      {/* Annual rollup */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Per jaar (werkgever)</Typography>
          <Metric label="Actieve gebruikers" value={r.activeUsers.toFixed(0)} />
          <Metric label="Gefinancierde sessies / jaar" value={r.fundedSessions.toFixed(0)} />
          <Metric label="Zelf-betaalde sessies / jaar" value={r.selfPaidSessions.toFixed(0)} />
          <Metric label="Werkgeversfactuur / jaar" value={eur(r.employerInvoice)} />
          <Metric label="Werkgeversfactuur / maand" value={eur(r.employerInvoice / 12)} />
          <Metric label="Realized kost / werknemer" value={eur(r.realizedPerEmp)} strong />
        </Card>
        <Card>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Onze cijfers</Typography>
          <Metric label="Onze marge / jaar" value={eur(r.ourMarginYr)} strong />
          <Metric label="Werknemers out-of-pocket / jaar" value={eur(r.employeeOOP)} />
          <Typography sx={{ fontSize: ".78rem", color: "text.secondary", mt: 1 }}>
            {r.selfPaidSessions === 0 ? "Zelf-betaald = 0: gem. sessies ≤ cap, dus niemand gaat over de cap." : "Werknemer-out-of-pocket = na mutualiteit-terugbetaling."}
          </Typography>
        </Card>
      </Box>

      {/* Adoption sensitivity */}
      <Card>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Adoptie-gevoeligheid</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>adoptie</TableCell>
              <TableCell align="right">gefinancierd/jaar</TableCell>
              <TableCell align="right">kost/werknemer</TableCell>
              <TableCell align="right">onze marge/jaar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {r.sens.map((s) => (
              <TableRow key={s.a}>
                <TableCell>{(s.a * 100).toFixed(0)}%</TableCell>
                <TableCell align="right">{s.funded.toFixed(0)}</TableCell>
                <TableCell align="right">{eur(s.costPerEmp)}</TableCell>
                <TableCell align="right">{eur(s.margin)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Stack>
  );
}
